import React, { useRef, useState } from "react";
import { X } from "lucide-react";
import Panel from "../ui/Panel/Panel.jsx";
import Button from "../ui/Button/Button.jsx";
import { downloadTemplate, parseUploadedFile, apiErrorMessage } from "../../utils/bulkImport.js";

/**
 * Generic "download a template, fill it in, upload it" bulk-add flow shared
 * by TrucksStep / AmendedReturnStep (mileage vehicles) / VinCorrectionStep.
 * Rows are inserted server-side through the same validation the manual
 * "+ Add ..." button already uses - either as ONE request via `bulkAdd`
 * (the real POST .../bulk endpoint - preferred whenever a backend return is
 * loaded) or, if that prop isn't given (the unauthenticated demo path with
 * no backend to call), by looping `addRow` once per row exactly as before.
 * This component owns no validation itself; it only does light client-side
 * pre-checks (via columns[].validate) so obviously-broken rows can be
 * skipped before hitting the network.
 *
 * columns: [{ key, header, required, example, description, parse?, validate? }]
 *   - parse(rawCellValue) -> normalized value (default: trimmed string)
 *   - validate(value, rowValues) -> "" | error message
 *
 * bulkAdd(rows) -> Promise<[{ index, ok, message? }]>, one entry per row in
 *   the same order as `rows`, matched back up by `index` (the backend
 *   returns each row's own outcome from a single request).
 *
 * templateId: a stable string unique to this table (e.g.
 *   "form2290-original-trucks-v1") stamped into the downloaded workbook and
 *   required to match on upload - stops one filing type's template from
 *   being accepted by another's uploader (see utils/bulkImport.js).
 */
const BulkImportModal = ({ open, onClose, title, columns, filename, sheetName, addRow, bulkAdd, templateId }) => {
  const [step, setStep] = useState("select"); // select | preview | importing | done
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]); // [{ values, errors: {key: msg}, ok }]
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]); // [{ values, ok, message }]
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate({ filename, sheetName, columns, templateId });
    } catch {
      setParseError("Couldn't generate the template. Please try again.");
    }
  };

  const reset = () => {
    setStep("select");
    setParseError("");
    setRows([]);
    setProgress({ done: 0, total: 0 });
    setResults([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (step === "importing") return;
    reset();
    onClose();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError("");
    try {
      const { rows: parsedRows } = await parseUploadedFile(file, columns, templateId);
      if (!parsedRows.length) {
        setParseError("That file doesn't have any data rows to import.");
        return;
      }
      const withStatus = parsedRows.map((values) => {
        const errors = {};
        columns.forEach((c) => {
          const msg = c.validate ? c.validate(values[c.key], values) : "";
          if (msg) errors[c.key] = msg;
        });
        return { values, errors, ok: Object.keys(errors).length === 0 };
      });
      setRows(withStatus);
      setStep("preview");
    } catch (err) {
      setParseError(err.message || "Couldn't read that file. Please try again.");
    }
  };

  const validRows = rows.filter((r) => r.ok);

  const startImport = async () => {
    setStep("importing");
    setProgress({ done: 0, total: validRows.length });

    if (bulkAdd) {
      // Single request for every row - the backend itself does one
      // transaction per row (partial success), so a bad row here still
      // doesn't undo rows already saved; we just get all the outcomes back
      // at once instead of one response per row.
      try {
        const outcomes = await bulkAdd(validRows.map((r) => r.values));
        const out = validRows.map((r, i) => {
          const outcome = outcomes.find((o) => o.index === i);
          return outcome
            ? { values: r.values, ok: outcome.ok, message: outcome.message }
            : { values: r.values, ok: false, message: "No result returned for this row." };
        });
        setResults(out);
      } catch (err) {
        // The request itself failed (network/auth/return-not-found) before
        // any per-row outcome existed - every row reports the same reason.
        const message = apiErrorMessage(err);
        setResults(validRows.map((r) => ({ values: r.values, ok: false, message })));
      }
      setProgress({ done: validRows.length, total: validRows.length });
      setStep("done");
      return;
    }

    const out = [];
    for (let i = 0; i < validRows.length; i++) {
      const { values } = validRows[i];
      try {
        await addRow(values);
        out.push({ values, ok: true });
      } catch (err) {
        out.push({ values, ok: false, message: apiErrorMessage(err) });
      }
      setProgress({ done: i + 1, total: validRows.length });
    }
    setResults(out);
    setStep("done");
  };

  if (!open) return null;

  const successCount = results.filter((r) => r.ok).length;
  const failCount = results.length - successCount;

  return (
    <div
      className="fixed inset-0 bg-[rgba(15,23,42,0.55)] backdrop-blur-[2px] flex items-center justify-center z-[200] p-4"
      onClick={handleClose}
    >
      <Panel padded className="w-full max-w-[720px] !p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-disp text-xl">{title}</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={step === "importing"}
            className="border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X size={18} />
          </button>
        </div>

        {step === "select" && (
          <div>
            <p className="text-[var(--muted)] text-sm mb-4">
              Download the template, fill in one row per vehicle, then upload it here. Every row gets the same
              validation as adding a vehicle by hand.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button variant="ghost" onClick={handleDownloadTemplate}>
                ⬇ Download template
              </Button>
            </div>
            <label className="block border-2 border-dashed border-[var(--line-2)] rounded-[var(--r-sm)] p-6 text-center cursor-pointer hover:border-[var(--accent)] transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleFile}
              />
              <span className="text-sm text-[var(--muted)]">Click to choose the filled-in .xlsx file to upload</span>
            </label>
            {parseError && <div className="text-[13px] text-[var(--danger)] mt-3">{parseError}</div>}
          </div>
        )}

        {step === "preview" && (
          <div>
            <p className="text-sm mb-3">
              <b>{validRows.length}</b> of {rows.length} row{rows.length === 1 ? "" : "s"} look ready to import
              {rows.length - validRows.length > 0 && (
                <> — {rows.length - validRows.length} will be skipped (see below).</>
              )}
            </p>
            <div className="max-h-[320px] overflow-y-auto border border-[var(--line)] rounded-[var(--r-sm)] mb-4">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-[var(--panel)]">
                  <tr>
                    <th className="text-left p-2 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)]">
                      #
                    </th>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        className="text-left p-2 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)]"
                      >
                        {c.header}
                      </th>
                    ))}
                    <th className="text-left p-2 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)]">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={r.ok ? "" : "bg-[color-mix(in_srgb,var(--danger)_6%,transparent)]"}>
                      <td className="p-2 border-b border-[var(--line)]">{i + 1}</td>
                      {columns.map((c) => (
                        <td key={c.key} className="p-2 border-b border-[var(--line)] font-mono">
                          {typeof r.values[c.key] === "boolean" ? (r.values[c.key] ? "Yes" : "No") : r.values[c.key] || "—"}
                        </td>
                      ))}
                      <td className="p-2 border-b border-[var(--line)]">
                        {r.ok ? (
                          <span className="text-[var(--ok)]">OK</span>
                        ) : (
                          <span className="text-[var(--danger)]">{Object.values(r.errors)[0]}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={reset}>
                ← Choose a different file
              </Button>
              <Button onClick={startImport} disabled={validRows.length === 0}>
                Import {validRows.length} vehicle{validRows.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        )}

        {step === "importing" && (
          <div className="py-6 text-center">
            <p className="text-[15px] mb-3">
              Importing {progress.done} of {progress.total}…
            </p>
            <div className="w-full h-2 bg-[var(--panel-2)] rounded-full overflow-hidden">
              <div
                className="h-full bg-[var(--accent)] transition-all duration-150"
                style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {step === "done" && (
          <div>
            <p className="text-[15px] mb-3">
              <b>{successCount}</b> imported
              {failCount > 0 && (
                <>
                  , <b className="text-[var(--danger)]">{failCount}</b> failed
                </>
              )}
              .
            </p>
            {failCount > 0 && (
              <div className="max-h-[260px] overflow-y-auto border border-[var(--line)] rounded-[var(--r-sm)] mb-4">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 bg-[var(--panel)]">
                    <tr>
                      <th className="text-left p-2 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)]">
                        Row
                      </th>
                      <th className="text-left p-2 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)]">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {results
                      .map((r, i) => ({ ...r, i }))
                      .filter((r) => !r.ok)
                      .map((r) => (
                        <tr key={r.i}>
                          <td className="p-2 border-b border-[var(--line)] font-mono">
                            {r.values.vin || r.values.oldVin || `#${r.i + 1}`}
                          </td>
                          <td className="p-2 border-b border-[var(--line)] text-[var(--danger)]">{r.message}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={reset}>
                Import another file
              </Button>
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default BulkImportModal;
